import * as React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import './user-list.scss'
import { Link } from 'react-router-dom'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { UserService } from '@halo/services/user.service'
import { Container } from 'typescript-ioc/es5'
import { useService } from '@halo/hooks/use-service'
import { UserDto } from '@halo/dtos/user.dto'
import { IApiResponse } from '@pushly/aqe/lib/interfaces'
import { Button, FilterBar, StatusBadge, StatusBadgeProps, Well } from '@pushly/aqe/lib/components'
import type { ActiveFilter, FilterDefinition } from '@pushly/aqe/lib/components'
import { simpleFormErrorNotification } from '@pushly/aqe/lib/utils'
import { classnames } from '@halo/_utils/classnames'
import { EyeOutlined, Loading3QuartersOutlined, LockOutlined, MailOutlined, UserOutlined } from '@ant-design/icons'
import { Divider, Flex, Form, Input, Modal, Select, Table } from 'antd'
import { AntdTableEmptyPlaceholder } from '@halo/components/antd-table-empty-placeholder/antd-table-empty-placeholder'
import { TableRowEntityDisplay } from '@halo/components/table-row-entity-display/table-row-entity-display'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '@halo/constants'
import { AccountDto } from '@halo/dtos/account.dto'
import { AccountService } from '@halo/services/account.service'
import { DomainService } from '@halo/services/domain.service'
import { UserType, UserTypeId } from '@halo/enums/user-type'
import * as deepEqual from 'react-fast-compare'
import { StatusType } from '@pushly/aqe/lib/enums'
import AccountUserModel from '@halo/models/access-control/account-user.model'
import {
    AccessRoleId,
    ExternalApiAccessRoleIds,
} from '@halo/enums/access-role.enum'
import { AccessRoleMap } from '@halo/models/access-role'
import { AbilityAction } from '@halo/enums/ability-action.enum'
import { asCaslSubject } from '@halo/stores/ability.state'
import { SubjectEntity } from '@halo/enums/ability-entity.enum'
import SwSimpleLinkPagination from '@halo/components/sw-simple-link-pagination/sw-simple-link-pagination'
import { IApiResponsePaginationLinks } from '@halo/interfaces/api-response-pagination-links'
import AccessControlService from '@halo/services/access-control.service'
import { UserAccountAccessView } from '@halo/components/user-data-form/user-access-well'

interface IAddUserPrompt {
    accountId?: number
    domainId?: number
    onOk: (domainDto: Partial<UserDto> & { permissions?: AccountUserModel[] }) => any
    onCancel: () => any
    visible: boolean
}

interface IAddUserPromptFormValues {
    userType?: UserType
    email?: string
}

export const AddUserPrompt: React.FC<IAddUserPrompt> = (props) => {
    const appState = useService<AppState>(AppState)
    // Preserve singleton resolution side-effect parity with the original class implementation.
    useMemo(() => Container.get(AppService), [])
    useMemo(() => Container.get(UserService), [])
    useMemo(() => Container.get(AccountService), [])
    useMemo(() => Container.get(DomainService), [])

    const [form] = Form.useForm<IAddUserPromptFormValues>()
    const [submitting, setSubmitting] = useState(false)
    const displayNameRef = useRef<any>(null)
    const [userAcs] = useState(() => AccessControlService.forUser(undefined as any))

    // Reactively re-render when userType changes so addonAfter / email rules /
    // available access roles update.
    const selectedUserType = (Form.useWatch('userType', form) ?? UserType.EXTERNAL) as UserType
    const selectedUserTypeIsInternal = selectedUserType === UserType.INTERNAL
    const actingUserIsInternal = appState.currentUser?.isInternalUser ?? false

    const noAccessRoleId = selectedUserTypeIsInternal
        ? AccessRoleId.INTERNAL_NO_ACCESS
        : AccessRoleId.EXTERNAL_NO_ACCESS

    const additionalEmailRules: any[] = []
    if (!selectedUserTypeIsInternal) {
        additionalEmailRules.push({
            type: 'email',
            message: 'A valid email address is required.',
        })
    }

    // Build a synthetic user object for UserAccountAccessView based on selected type
    const syntheticUser = useMemo(() => {
        const dto = new UserDto()
        dto.user_type_id = selectedUserTypeIsInternal ? UserTypeId.INTERNAL : UserTypeId.EXTERNAL
        dto.user_type = selectedUserType
        dto.user_role_id = selectedUserTypeIsInternal ? AccessRoleId.INTERNAL_NO_ACCESS : AccessRoleId.EXTERNAL_NO_ACCESS
        dto.flags = []
        return dto
    }, [selectedUserType, selectedUserTypeIsInternal])

    // Autofocus the email input while the modal is visible, mirroring the legacy retry loop.
    useEffect(() => {
        if (!props.visible) return
        let cancelled = false
        const tryFocus = () => {
            if (cancelled) return
            if (!displayNameRef.current) {
                setTimeout(tryFocus, 50)
                return
            }
            if (props.accountId) {
                displayNameRef.current.focus()
            }
        }
        tryFocus()
        return () => {
            cancelled = true
        }
    }, [props.visible, props.accountId])

    const handleSubmit = async (formValues: IAddUserPromptFormValues) => {
        const dto: Partial<UserDto> & Partial<{ permissions: AccountUserModel[] }> = {}
        dto.user_type = formValues.userType
        dto.email = formValues.email

        // ensure @pushly.com added to internal users
        if (dto.user_type === UserType.INTERNAL) {
            dto.email = (dto.email ?? '').replace(/@.+/gi, '') + '@pushly.com'
        }

        const permissions: AccountUserModel[] = []
        if (props.accountId) {
            // Collect account + domain permissions from the access control service
            const dirtyRecords = userAcs.getDirtyRecords()
            const dirtyAccount = dirtyRecords.accounts[props.accountId]
            const domainRecords = Object.values(dirtyRecords.domains)
            const noAccessRoles = [AccessRoleId.INTERNAL_NO_ACCESS, AccessRoleId.EXTERNAL_NO_ACCESS]
            const assignedDomains = domainRecords.filter((d) => !noAccessRoles.includes(d.getRoleId()))

            if (assignedDomains.length === 0) {
                simpleFormErrorNotification({
                    errorFields: [{ name: ['domains'], errors: ['At least one domain must have access assigned.'] }],
                })
                return
            }

            const orgRoleId = dirtyAccount?.getRoleId() ?? noAccessRoleId
            const orgRole = AccessRoleMap.get(orgRoleId)
            const acctModel = new AccountUserModel({
                account_id: props.accountId,
                user_id: undefined as any,
                role_id: orgRoleId,
                role_name: orgRole?.name ?? '',
                flags: [],
            })

            acctModel.setDomainRecords(assignedDomains)
            permissions.push(acctModel)
        }

        dto.permissions = permissions

        await props.onOk(dto)
    }

    const handleOk = async () => {
        let values: IAddUserPromptFormValues
        try {
            values = await form.validateFields()
        } catch (err) {
            // v6 validateFields rejects with { errorFields, values, outOfDate };
            // aqe's simpleFormErrorNotification handles both legacy and v6 shapes.
            simpleFormErrorNotification(err)
            return
        }

        setSubmitting(true)
        try {
            await handleSubmit(values)
        } finally {
            setSubmitting(false)
        }
    }

    const handleCancel = () => {
        props.onCancel()
    }

    return (
        <Modal
            className={classnames('add-user-prompt')}
            title="Add User"
            open={props.visible}
            onOk={handleOk}
            okText="Submit"
            confirmLoading={submitting}
            onCancel={handleCancel}
            centered={true}
            width={800}
            styles={{ body: { maxHeight: 'calc(100dvh - 220px)', overflowY: 'auto' } }}
        >
            <Well className="nested" mode="ghost" hideHeader={true} hideFooter={true}>
                <Form
                    form={form}
                    {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}
                    labelAlign="left"
                    className="hide-inline-errors"
                    onFinish={handleSubmit}
                >
                    <Form.Item
                        label="User Type"
                        name="userType"
                        initialValue={UserType.EXTERNAL}
                        rules={[
                            {
                                required: true,
                                message: 'User type is required.',
                            },
                        ]}
                    >
                        <Select<UserType> size="small">
                            <Select.Option value={UserType.EXTERNAL}>External Platform User</Select.Option>
                            {actingUserIsInternal && (
                                <Select.Option value={UserType.INTERNAL}>Internal Platform User</Select.Option>
                            )}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Email Address"
                        name="email"
                        rules={[
                            {
                                required: true,
                                message: 'Email address is required.',
                            },
                            ...additionalEmailRules,
                        ]}
                    >
                        <Input
                            ref={displayNameRef}
                            size="small"
                            autoComplete="off"
                            addonAfter={
                                selectedUserTypeIsInternal && (
                                    <span>
                                        <b>@pushly.com</b>
                                    </span>
                                )
                            }
                        />
                    </Form.Item>
                </Form>

                {props.accountId && (
                    <>
                        <Divider style={{ margin: '12px 0' }} />
                        <UserAccountAccessView
                            mode="nested"
                            user={syntheticUser}
                            userAcs={userAcs}
                            account={{ id: props.accountId } as any}
                        />
                    </>
                )}
            </Well>
        </Modal>
    )
}

interface IUserList {
    options: {
        account?: AccountDto
    }
}

interface IUserListState {
    loading: boolean
    users: UserDto[]
    usersMeta: any
    page: number
    pageSize: number
    query?: string
    typeFilter: string[]
    statusFilter: string[]
    paginationLinks?: IApiResponsePaginationLinks

    showAddUserPrompt: boolean
}

export class UserList extends React.Component<IUserList, IUserListState> {
    protected appState: AppState
    protected appService: AppService
    protected userService: UserService

    public constructor(props: any) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.userService = Container.get(UserService)

        const typeFilter = [UserType.EXTERNAL, UserType.EXTERNAL_API]
        // default external user view to include internal users in default filter
        // - internal_api users should never be visible to external view
        if (!this.appState.currentUser?.isInternalUser) {
            typeFilter.push(UserType.INTERNAL)
        }

        this.state = {
            loading: true,
            users: [],
            usersMeta: {},
            page: 1,
            pageSize: 15,
            showAddUserPrompt: false,
            typeFilter,
            statusFilter: [
                StatusType[StatusType.ACTIVE],
                StatusType[StatusType.INVITED],
                StatusType[StatusType.PASSWORD_RESET],
            ],
        }
    }

    public componentDidMount(): void {
        this.fetchUsers()
    }

    public render() {
        const userTypeOptions = [
            { value: UserType.EXTERNAL, label: 'External' },
            { value: UserType.EXTERNAL_API, label: 'External (API)' },
        ]

        if (this.appState.currentUser?.isInternalUser) {
            userTypeOptions.push(
                { value: UserType.INTERNAL, label: 'Internal' },
                { value: UserType.INTERNAL_API, label: 'Internal (API)' },
            )
        }

        const filterDefinitions: FilterDefinition[] = [
            {
                key: 'type',
                label: 'User Type',
                type: 'multi-select',
                showOperatorSelect: false,
                showSearch: false,
                selectOptions: userTypeOptions,
            },
            {
                key: 'status',
                label: 'Status',
                type: 'multi-select',
                showOperatorSelect: false,
                showSearch: false,
                selectOptions: [
                    { value: StatusType[StatusType.ACTIVE], label: 'Active' },
                    { value: StatusType[StatusType.DISABLED], label: 'Disabled' },
                    { value: StatusType[StatusType.INVITED], label: 'Invited' },
                    { value: StatusType[StatusType.PASSWORD_RESET], label: 'Password Reset' },
                ],
            },
        ]

        const getFilterBarValue = (): ActiveFilter[] => {
            const active: ActiveFilter[] = []
            if (this.state.typeFilter.length > 0) {
                active.push({ key: 'type', operator: 'in', value: this.state.typeFilter })
            }
            if (this.state.statusFilter.length > 0) {
                active.push({ key: 'status', operator: 'in', value: this.state.statusFilter })
            }
            return active
        }

        const handleFilterBarApply = (filters: ActiveFilter[]) => {
            const nextType = (filters.find((f) => f.key === 'type')?.value as string[]) ?? []
            const nextStatus = (filters.find((f) => f.key === 'status')?.value as string[]) ?? []
            this.filterUserTypes(nextType)
            this.filterUserStatuses(nextStatus)
        }

        const tableHeader = (
            <Flex className="user-list-header" gap={8} justify="space-between" align="center">
                <Flex flex={1} gap={8} align="center">
                    <Input.Search
                        className="user-list-search"
                        style={{ width: 300 }}
                        size="middle"
                        placeholder="Search by ID or Username"
                        enterButton={true}
                        onSearch={this.searchUsers}
                    />
                    <FilterBar
                        filterDefinitions={filterDefinitions}
                        value={getFilterBarValue()}
                        onApply={handleFilterBarApply}
                        showInlineTags={false}
                        showInlineClearAll={false}
                        size="middle"
                    />
                </Flex>
                {this.props.options.account && (
                    <Button size="middle" type="primary" onClick={this.showAddUserPrompt}>
                        New User
                    </Button>
                )}
            </Flex>
        )

        return (
            <div className={classnames('user-list')}>
                <Table<UserDto>
                    className="aqe-table-well"
                    title={() => tableHeader}
                    size="small"
                    bordered={false}
                    dataSource={this.state.users}
                    pagination={false}
                    footer={(currentTableData) => (
                        <SwSimpleLinkPagination
                            currentTableData={currentTableData}
                            links={this.state.paginationLinks}
                            onPrev={(_ev, _link, config) => {
                                this.setState({ page: config.current! }, () => this.fetchUsers())
                            }}
                            onNext={(_ev, _link, config) => {
                                this.setState({ page: config.current! }, () => this.fetchUsers())
                            }}
                        />
                    )}
                    rowKey={(record) => record.id.toString()}
                    loading={
                        this.state.loading && {
                            indicator: <Loading3QuartersOutlined spin={true} />,
                        }
                    }
                    locale={{
                        emptyText: <AntdTableEmptyPlaceholder text="No Users" icon={<UserOutlined />} />,
                    }}
                >
                    {this.appState.currentUser?.isInternalUser && (
                        <Table.Column className="id" title="ID" dataIndex="id" />
                    )}
                    <Table.Column className="user" title="User" render={this.renderUserColumn} />
                    <Table.Column className="type" title="Type" render={this.renderUserTypeColumn} />
                    <Table.Column className="status" title="Status" render={this.renderStatusColumn} />
                    <Table.Column
                        className="actions"
                        title="Actions"
                        align="center"
                        render={this.renderRowActions}
                    />
                </Table>

                <AddUserPrompt
                    key={`${this.state.showAddUserPrompt ? 'v' : 'h'}-add-user`}
                    visible={this.state.showAddUserPrompt}
                    accountId={this.props.options.account?.id ? this.props.options.account.id : undefined}
                    onOk={this.handleUserAdd}
                    onCancel={this.hideAddUserPrompt}
                />
            </div>
        )
    }

    protected get paginationConfig(): any {
        return {
            pageSize: this.state.pageSize,
            total: this.state.usersMeta.total,
            current: this.state.page,
            showSizeChanger: false,
            size: 'small',
            simple: true,
            showTotal: (total: number) => (
                <span>
                    ${total} {total === 1 ? 'User' : 'Users'}
                </span>
            ),
            itemRender: (current: any, type: string, originalElement: any) => {
                if (type === 'prev') {
                    return <a>Previous</a>
                }
                if (type === 'next') {
                    return <a>Next</a>
                }

                return originalElement
            },
            onChange: async (page: number, pageSize: number) => {
                await this.setState({ page })
                await this.fetchUsers()
            },
        }
    }

    protected renderUserColumn = (_: string, user: UserDto): React.ReactNode => {
        const name: string = user.name || user.email

        return <TableRowEntityDisplay title={<Link to={this.buildUserUrl(user)}>{name}</Link>} status={user.email} />
    }

    protected renderUserTypeColumn = (_: string, user: UserDto): React.ReactNode => {
        let text: any = '--'

        switch (user.user_type_id) {
            case UserTypeId.INTERNAL:
                text = 'Internal'
                break
            case UserTypeId.EXTERNAL:
                text = 'External'
                break
        }

        if (
            user.user_role_id === AccessRoleId.INTERNAL_API_USER ||
            ExternalApiAccessRoleIds.includes(user.user_role_id)
        ) {
            text = 'API User'
        }

        return text
    }

    protected renderStatusColumn = (_: string, user: UserDto): React.ReactNode => {
        return <StatusBadge type="user" status={user.status as StatusBadgeProps['status']} expanded={true} />
    }

    protected renderRowActions = (_: any, user: UserDto): React.ReactNode => {
        const isSelf = this.appState.currentUser?.id === user.id
        const userDisabled = user.status_id === StatusType.DISABLED
        const showReInvite = this.props.options?.account?.id && !isSelf && user.status_id === StatusType.INVITED
        const showResetPassword =
            this.props.options?.account?.id && !isSelf && user!.status_id !== StatusType.INVITED && !user.isApiUser
        const showDisableUser = !isSelf && (this.appState.currentUser?.isInternalUser || !user.isInternalUser)
        const canUpdateUser =
            this.props.options.account &&
            this.appState.abilityStore.can(
                AbilityAction.UPDATE,
                asCaslSubject(SubjectEntity.USER, {
                    ...user,
                    userTypeId: user.user_type_id,
                    accountId: this.props.options.account.id,
                }),
            )

        const actions: any[] = []

        if (canUpdateUser) {
            if (userDisabled) {
                actions.push({
                    text: 'Enable',
                    icon: <EyeOutlined />,
                    onClick: this.handleEnable,
                    confirm: {
                        okText: 'Enable',
                        title: 'Enable User',
                        iconType: 'question-circle',
                        content: <>Are you sure you want to enable this user?</>,
                    },
                    user,
                })
            } else {
                if (showDisableUser) {
                    actions.push({
                        text: 'Disable',
                        icon: <EyeOutlined />,
                        onClick: this.handleDisable,
                        confirm: {
                            okText: 'Disable',
                            title: 'Disable User',
                            iconType: 'warning',
                            content: <>Are you sure you want to disable this user?</>,
                        },
                        user,
                    })
                }
                if (showReInvite) {
                    actions.push({
                        text: 'Re-Invite',
                        icon: <MailOutlined />,
                        onClick: this.handleReInvite,
                        user,
                    })
                }
                if (showResetPassword) {
                    actions.push({
                        text: 'Password Reset',
                        icon: <LockOutlined />,
                        onClick: this.handleResetPassword,
                        user,
                    })
                }
            }
        }

        return (
            <Button
                size="small"
                altHref={this.buildUserUrl(user)}
                onClick={() => this.jumpToUser(user)}
                actions={actions.length > 0 ? actions : undefined}
            >
                View
            </Button>
        )
    }

    protected buildUserUrl(user: UserDto): string {
        let url = `/users/${user.id}`

        const accountId = this.props.options.account?.id
        if (accountId) {
            url = `/organizations/${accountId}${url}`
        }

        return url
    }

    protected jumpToUser(user: UserDto): void {
        this.appService.route(this.buildUserUrl(user), {
            account: this.props.options.account,
            user,
        })
    }

    protected showAddUserPrompt = async (): Promise<void> => {
        return this.setState({ showAddUserPrompt: true })
    }

    protected hideAddUserPrompt = async (): Promise<void> => {
        return this.setState({ showAddUserPrompt: false })
    }

    protected handleUserAdd = async (
        userDto: Partial<UserDto> & { permissions?: AccountUserModel[] },
    ): Promise<void> => {
        let accounts: any[] = []

        if (Array.isArray(userDto.permissions)) {
            const source = userDto.permissions[0]
            const accountRecord: any = {
                accountId: source.getAccountId(),
                roleId: source.getRoleId(),
                flags: source.getFlags(),
                domains: {},
            }

            for (const domain of Array.from(source.getDomainRecords().values())) {
                accountRecord.domains[domain.getDomainId()] = {
                    roleId: domain.getRoleId(),
                    flags: domain.getFlags(),
                }
            }

            accounts.push(accountRecord)
        }

        const { ok, data: user } = await this.userService.addUser(
            { ...userDto, accounts },
            {
                // The Add User modal shows its own in-modal submit spinner
                // (confirmLoading); suppress the full-screen app loader so the
                // background doesn't flash behind the modal mask while saving.
                showLoadingScreen: false,
            },
            this.appState,
        )

        if (ok) {
            this.jumpToUser(user)
        }
    }

    protected handleDisable = async (item: any) => {
        const { user } = item.props

        await this.userService.updateUserById(
            user.id,
            {
                status: 'DISABLED',
            },
            {
                showLoadingScreen: true,
            },
        )

        return this.fetchUsers()
    }

    protected handleEnable = async (item: any) => {
        const { user } = item.props

        await this.userService.updateUserById(
            user.id,
            {
                status: 'ACTIVE',
            },
            {
                showLoadingScreen: true,
            },
        )

        return this.fetchUsers()
    }

    protected handleReInvite = async (item: any): Promise<void> => {
        const { user } = item.props

        await this.userService.triggerInviteForUserId(
            user.id,
            this.props.options.account.id,
            {
                showLoadingScreen: true,
            },
            this.appState,
        )

        return this.fetchUsers()
    }

    protected handleResetPassword = async (item: any): Promise<void> => {
        const { user } = item.props
        await this.userService.triggerPasswordResetForUserId(
            user.id,
            this.props.options.account.id,
            {
                showLoadingScreen: true,
            },
            this.appState,
        )
        return this.fetchUsers()
    }

    protected filterUserTypes = async (typeFilter: string[]) => {
        if (!deepEqual(typeFilter, this.state.typeFilter)) {
            typeFilter = typeFilter || []
            await this.setState({ typeFilter })

            return this.fetchUsers()
        }
    }

    protected filterUserStatuses = async (statusFilter: string[]) => {
        if (!deepEqual(statusFilter, this.state.statusFilter)) {
            statusFilter = statusFilter || []
            await this.setState({ statusFilter })

            return this.fetchUsers()
        }
    }

    protected searchUsers = async (value: string): Promise<void> => {
        let query = value.trim()

        if (value.length < 2) {
            query = undefined
        }

        await this.setState({ query })

        return this.fetchUsers()
    }

    protected async fetchUsers(): Promise<void> {
        let update: any = { loading: true }
        await this.setState(update)

        update.loading = false
        update.users = []

        let response: IApiResponse<any[]>
        const options = {
            showLoadingScreen: false,
            query: {
                page: this.state.page,
                limit: this.state.pageSize,
                search: this.state.query,
                type: this.state.typeFilter.join(','),
                status: this.state.statusFilter.join(','),
            },
        }

        if (this.props.options.account?.id) {
            response = await this.userService.fetchAllAccountUsers(this.props.options.account.id, options)
        } else {
            response = await this.userService.fetchAll(options)
        }

        if (response && response.ok) {
            update.users = response.data
            update.paginationLinks = response.meta.links ?? this.state.paginationLinks
            update.usersMeta = response.meta
        }

        return this.setState(update)
    }
}
